import styles from "./style.module.scss";
import { useRef, forwardRef, useImperativeHandle, useEffect, useState } from "react";
import DynamicForm, { DynamicFormRef, SELECT_INPUT_TYPE, setCurrencySymbolForMoneyInputs } from "@mavichpe/dynamic-forms";
import InvoicesRecibeFormConfig from "./form";
import { InvoicesRecibeDataType } from "@/formTypes/InvoicesRecibeFormDataType";
import { CurrencyIdPubSub, CurrencyUpdatePubSubPayload } from "@utils/pubsubs/PubSub";
import { CurrencyApi } from "@/api/CurrencyApi";
import { setCurrency } from "@/store/slices/currencySlides";
import { useAppDispatch } from "@/store/hooks";
import { removeFunctionFromObject } from "@utils/Utils";
import { CURRENCIES_SYMBOLS, RECIBE_DOCUMENT_INVOICE_TYPE_KEY, RECIBE_DOCUMENT_SETTLEMENT_TYPE_KEY, RECIBE_TYPE_NUMBER_SEPARATOR } from "@utils/Constants";
import { DocumentsEntryType } from "@components/dynamic/DocumentStatusList";
import debounce from "debounce";
import { showAlert } from "@utils/Alert";

type InvoicesRecibeFormProps = {
  onInvoiceRecibeSubmit: (data: InvoicesRecibeDataType) => void;
  onRecibeDateChange: (projectDate: string) => void;
  defaultData?: InvoicesRecibeDataType;
  innerForm?: boolean;
  recibeTotal: number;
  selectedDocuments: DocumentsEntryType[],
  manualRecibe?: boolean
};

// Usamos forwardRef para permitir ref externo
const InvoicesRecibeForm = forwardRef<DynamicFormRef, InvoicesRecibeFormProps>(
  (props, ref) => {
    const { onInvoiceRecibeSubmit, innerForm = false, defaultData,
      selectedDocuments, recibeTotal, onRecibeDateChange, manualRecibe = false } = props;
    const dispatch = useAppDispatch();
    const [currencySymbol, setCurrencySymbol] = useState<string>("");
    const [isRecibeInFull, setRecibeInFull] = useState<boolean>(true);

    const internalRef = useRef<DynamicFormRef>(null);
    useImperativeHandle(ref, () => internalRef.current as DynamicFormRef, []);


    useEffect(() => {
      const unsubscribe = CurrencyIdPubSub.subscribe(setFormCurrency);
      return () => {
        unsubscribe();
      };
    }, []);

    const setFormCurrency = async (payload: CurrencyUpdatePubSubPayload) => {
      const { currencyId } = payload;
      const priceData = await CurrencyApi.get(currencyId);
      if (priceData.currency_id) {
        dispatch(setCurrency(removeFunctionFromObject(priceData)));

        setCurrencySymbol(CURRENCIES_SYMBOLS[priceData.currency_id])
        if (internalRef) {
          internalRef.current?.setFormValue("multiple",
            {
              currencyId,
              exchange_rate: priceData.exchange_rate

            }, "multipleData")
        }
      }
    }

    const getFormConfig = () => {
      let config = {
        ...InvoicesRecibeFormConfig
      }

      if (currencySymbol) {
        config = setCurrencySymbolForMoneyInputs(InvoicesRecibeFormConfig, currencySymbol)
      }

      if (!manualRecibe) {
        const recibeInput = config.sections.flatMap(s => s.fields).flat().find(f => f.name === "numero")
        if (recibeInput) {
          recibeInput.type = "hidden";
          delete recibeInput.validation
        }
      }
      const factura_abonoInput = config.sections.flatMap(s => s.fields).flat().find(f => f.name === "factura_abono")
      if (factura_abonoInput) {
        if (isRecibeInFull) {
          factura_abonoInput.type = "hidden"
        } else {
          factura_abonoInput.type = SELECT_INPUT_TYPE
          // Un saldo a favor no recibe abonos: no puede ser el documento del abono parcial.
          const payableDocuments = selectedDocuments.filter(d => d.kind == "invoice" || d.kind == "settlement");
          if (payableDocuments.length > 0) {
            const invoiceOptions = payableDocuments.reduce((a, v) => {
              return {
                ...a,
                [`${v.kind == "invoice" ? `${RECIBE_DOCUMENT_INVOICE_TYPE_KEY}${RECIBE_TYPE_NUMBER_SEPARATOR}` : `${RECIBE_DOCUMENT_SETTLEMENT_TYPE_KEY}${RECIBE_TYPE_NUMBER_SEPARATOR}`}${v.data.id}`]: `${v.kind == "invoice" ? "Factura #" : "Liquidacion #"}${v.data.numero}`
              }
            }, {})
            factura_abonoInput.options = invoiceOptions
          }
        }
      }
      return config
    }

    const checkReciboFullPayment = debounce((amount: string) => {
      const newRecibeAmount = Number(amount);
      if (newRecibeAmount > recibeTotal) {
        showAlert("danger", "El monto total del recibo no puede ser superior al monto total de los documents")
        internalRef.current?.setFormValue("monto", recibeTotal, "decimal")
      } else {
        if (newRecibeAmount == recibeTotal) {
          setRecibeInFull(true)
        } else {
          setRecibeInFull(false)
        }
      }
    }, 300);

    const onInputChange = (name: keyof InvoicesRecibeDataType, value: any) => {
      if (name == "monto") {
        checkReciboFullPayment(`${value}`)
      } else if (name == "fecha") {
        onRecibeDateChange(`${value}`)
      }
    }

    return (
      <DynamicForm<InvoicesRecibeDataType>
        externalFormState={defaultData}
        ref={internalRef}
        innerForm={innerForm}
        formConfig={getFormConfig()}
        onInputChange={onInputChange}
        onSubmit={onInvoiceRecibeSubmit}
      />
    );
  }
);

export default InvoicesRecibeForm;